import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export const controlBase =
  'w-full rounded-lg border bg-surface text-ink placeholder:text-ink-faint shadow-sm shadow-black/[0.02] transition-[border-color,box-shadow] duration-150 outline-none focus-visible:outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-500/15 disabled:cursor-not-allowed disabled:bg-subtle disabled:text-ink-muted read-only:bg-subtle/60';

export function controlState(invalid?: boolean): string {
  return invalid
    ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-500/15 dark:border-rose-500/60'
    : 'border-line-strong';
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
  inputSize?: 'sm' | 'md' | 'lg';
}

const INPUT_SIZES = { sm: 'h-8 px-2.5 text-[13px]', md: 'h-9 px-3 text-sm', lg: 'h-11 px-3.5 text-[15px]' };

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid, inputSize = 'md', ...props },
  ref,
) {
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(controlBase, controlState(invalid), INPUT_SIZES[inputSize], className)}
      {...props}
    />
  );
});

export interface InputGroupProps extends Omit<InputProps, 'prefix'> {
  prefix?: ReactNode;
  suffix?: ReactNode;
  wrapperClassName?: string;
}

/** Input with leading/trailing adornments (currency symbol, icons, units). */
export const InputGroup = forwardRef<HTMLInputElement, InputGroupProps>(function InputGroup(
  { prefix, suffix, wrapperClassName, className, ...props },
  ref,
) {
  return (
    <div className={cn('relative flex items-center', wrapperClassName)}>
      {prefix ? (
        <span className="pointer-events-none absolute left-3 flex items-center text-ink-faint [&_svg]:size-4">
          {prefix}
        </span>
      ) : null}
      <Input ref={ref} className={cn(prefix ? 'pl-9' : '', suffix ? 'pr-12' : '', className)} {...props} />
      {suffix ? (
        <span className="absolute right-3 flex items-center text-xs text-ink-faint">{suffix}</span>
      ) : null}
    </div>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, invalid, rows = 3, ...props },
  ref,
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      aria-invalid={invalid || undefined}
      className={cn(
        controlBase,
        controlState(invalid),
        'min-h-[72px] resize-y px-3 py-2 text-sm leading-relaxed',
        className,
      )}
      {...props}
    />
  );
});

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
  selectSize?: 'sm' | 'md' | 'lg';
}

const SELECT_SIZES = {
  sm: 'h-8 pl-2.5 pr-8 text-[13px]',
  md: 'h-9 pl-3 pr-9 text-sm',
  lg: 'h-11 pl-3.5 pr-10 text-[15px]',
};

/** `className` sizes the wrapper (e.g. `w-[160px]`) so fixed widths hold inside flex toolbars. */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, invalid, selectSize = 'md', children, ...props },
  ref,
) {
  return (
    <div className={cn('relative w-full', className)}>
      <select
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(
          controlBase,
          controlState(invalid),
          SELECT_SIZES[selectSize],
          'cursor-pointer appearance-none',
        )}
        {...props}
      >
        {children}
      </select>
      <ChevronDown
        className="pointer-events-none absolute right-2.5 top-1/2 size-4 -translate-y-1/2 text-ink-faint"
        aria-hidden
      />
    </div>
  );
});
